// Settings card — Email digest preferences + preview/test.
//
// Lets the operator:
//   - Toggle weekly digest on/off (and pick frequency)
//   - Preview the current digest (HTML rendered in an iframe)
//   - Send themselves a test digest immediately
//   - See when the last digest was sent

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Mail, RefreshCcw, Send, Eye } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const fmtAgo = (iso) => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function AgentDigestPanel({ currentUser, showToast = () => {} }) {
  const [prefs, setPrefs]   = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy]     = React.useState(false);
  const [previewHtml, setPreviewHtml] = React.useState(null);

  const loadPrefs = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from('subscriptions')
      .select('email_digest_enabled, email_digest_frequency, email_digest_last_sent, email, email_digest_sections')
      .eq('owner_id', currentUser.id)
      .maybeSingle();
    setPrefs(data || { email_digest_enabled: true, email_digest_frequency: 'weekly', email_digest_last_sent: null, email_digest_sections: null });
    setLoading(false);
  }, [currentUser?.id]);

  // Toggle one section on/off in email_digest_sections. NULL means
  // "all on" (back-compat); first toggle migrates to an explicit
  // object with all known keys true so subsequent toggles only need
  // to flip the named key.
  const toggleSection = (key, freq) => {
    const knownKeys = freq === 'daily'
      ? ['commission', 'drafts_today', 'ai_activity', 'birthdays']
      : ['commission', 'ai_activity', 'top_carrier', 'birthdays', 'renewals', 'pipeline'];
    const current = prefs?.email_digest_sections || Object.fromEntries(knownKeys.map(k => [k, true]));
    const next = { ...current, [key]: current[key] === false ? true : false };
    save({ email_digest_sections: next });
  };

  React.useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const save = async (patch) => {
    setBusy(true);
    const next = { ...prefs, ...patch };
    const { error } = await supabase.from('subscriptions').update(patch).eq('owner_id', currentUser.id);
    if (error) showToast(`Save failed: ${error.message}`);
    else { setPrefs(next); showToast('Saved.'); }
    setBusy(false);
  };

  const preview = async () => {
    setBusy(true);
    try {
      const f = prefs?.email_digest_frequency || 'weekly';
      const r = await apiFetch(`/api/digests/preview?frequency=${encodeURIComponent(f)}`);
      setPreviewHtml(r.html);
    } catch (err) { showToast(`Preview failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const f = prefs?.email_digest_frequency || 'weekly';
      const r = await apiFetch('/api/digests/preview', {
        method: 'POST',
        body: JSON.stringify({ frequency: f }),
      });
      if (r.sent)         showToast(`Test sent to ${prefs?.email || 'your inbox'}.`);
      else if (r.dry_run) showToast('Dry-run (RESEND_API_KEY not set). No email actually sent.');
      else                showToast(`Email failed: ${r.error || 'unknown'}`);
    } catch (err) { showToast(`Send failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="bg-white rounded-xl border border-slate-200 p-5 text-sm text-slate-500">Loading digest preferences…</div>;

  // Header copy + description reacts to the currently selected frequency
  // so the operator never sees "Every Monday morning…" when they've
  // picked Daily. Off-state surfaces a one-line nudge instead.
  const freq = prefs?.email_digest_frequency || 'weekly';
  const titleByFreq = {
    daily:   'Daily business digest',
    weekly:  'Weekly business digest',
    monthly: 'Monthly business digest',
    off:     'Business digest (off)',
  };
  const descByFreq = {
    daily:   `Every morning at 8am Eastern we'll email you yesterday's commission, today's draft-day callouts, what your AI did, and any clients waiting on you.`,
    weekly:  `Every Monday morning we'll email you a summary of last week's commission, AI agent activity (texts sent / calls / appointments), and the renewals coming up so nothing slips.`,
    monthly: `On the 1st of each month we'll email you a summary of last month's commission, AI activity, and top-performing carriers — strategic snapshot rather than action list.`,
    off:     `Digests are turned off. Switch to Daily, Weekly, or Monthly to start receiving them.`,
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-amber-600"/>
          <h3 className="font-bold text-slate-900">{titleByFreq[freq] || titleByFreq.weekly}</h3>
        </div>
        <button onClick={loadPrefs} className="text-slate-400 hover:text-slate-700"><RefreshCcw size={14}/></button>
      </div>
      <p className="text-xs text-slate-500">
        {descByFreq[freq] || descByFreq.weekly}
        {freq !== 'off' && <> Sent to <strong>{prefs?.email || currentUser?.email}</strong>.</>}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox"
            checked={prefs?.email_digest_enabled !== false}
            onChange={(e) => save({ email_digest_enabled: e.target.checked })}
            disabled={busy}/>
          Enable digest emails
        </label>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Frequency</label>
          <select
            value={prefs?.email_digest_frequency || 'weekly'}
            onChange={(e) => save({ email_digest_frequency: e.target.value })}
            disabled={busy}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
          >
            <option value="daily">Daily (every morning 8am Eastern)</option>
            <option value="weekly">Weekly (Monday morning)</option>
            <option value="monthly">Monthly (1st of month)</option>
            <option value="off">Off</option>
          </select>
        </div>
      </div>

      {/* Section toggles — operator picks which blocks appear in
          their digest. NULL prefs = all-on (back-compat). */}
      {freq !== 'off' && (
        <div className="pt-2 border-t border-slate-100 mt-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
            Sections in this digest
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {(freq === 'daily'
              ? [
                  { key: 'commission',   label: "Yesterday's commission" },
                  { key: 'drafts_today', label: 'Drafts this week' },
                  { key: 'ai_activity',  label: 'What your AI did' },
                  { key: 'birthdays',    label: 'Birthdays this week' },
                ]
              : [
                  { key: 'commission',  label: 'Commission earned' },
                  { key: 'ai_activity', label: 'AI agents activity' },
                  { key: 'top_carrier', label: 'Top carrier' },
                  { key: 'birthdays',   label: 'Birthdays' },
                  { key: 'renewals',    label: 'Upcoming renewals' },
                  { key: 'pipeline',    label: 'Pipeline' },
                ]
            ).map(s => {
              const current = prefs?.email_digest_sections;
              const on = !current || current[s.key] !== false;
              return (
                <label key={s.key} className="flex items-center gap-2 text-xs cursor-pointer text-slate-700">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleSection(s.key, freq)}
                    disabled={busy}
                  />
                  {s.label}
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap pt-2">
        <button onClick={preview} disabled={busy} className="text-xs font-bold uppercase tracking-wider bg-slate-900 hover:bg-black text-white rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50">
          <Eye size={12}/> Preview now
        </button>
        <button onClick={sendTest} disabled={busy} className="text-xs font-bold uppercase tracking-wider bg-amber-600 hover:bg-amber-700 text-white rounded-md px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50">
          <Send size={12}/> Send me a test
        </button>
        <span className="text-xs text-slate-500 ml-auto">Last sent: <strong>{fmtAgo(prefs?.email_digest_last_sent)}</strong></span>
      </div>

      {previewHtml && (
        <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
          <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center justify-between">
            <span>Preview — what would land in your inbox today</span>
            <button onClick={() => setPreviewHtml(null)} className="text-slate-400 hover:text-slate-700">close</button>
          </div>
          <iframe
            title="Digest preview"
            srcDoc={previewHtml}
            className="w-full h-[600px] border-0"
          />
        </div>
      )}
    </div>
  );
}
